export type AreaId = 'arbeidsgiver' | 'arbeidstaker' | 'samarbeidspartner'
export type AppLocale = 'nb' | 'nn' | 'en'

export const defaultLocale: AppLocale = 'nb'
export const locales: AppLocale[] = ['nb', 'nn', 'en']
const areaIds: AreaId[] = ['arbeidsgiver', 'arbeidstaker', 'samarbeidspartner']

/** Dekoratøren bruker egne navn på områdene. */
export const decoratorContexts = {
    arbeidsgiver: 'arbeidsgiver',
    arbeidstaker: 'privatperson',
    samarbeidspartner: 'samarbeidspartner',
} as const satisfies Record<AreaId, string>

/** Ikke satt lokalt, der alle områdene serves fra samme origin uten basePath. */
export const currentArea = areaIds.find((area) => area === process.env.NEXT_PUBLIC_APP_AREA)

/** TODO: midlertidig mål for lenker vi ennå ikke har adressen til. Erstatt med ekte URL. */
export const PLACEHOLDER_URL = 'https://fake.url.com'

/** Bakt inn ved bygg, fra base_path_* i deploy.dev.yaml. Tom lokalt. */
export const basePath = process.env.NEXT_PUBLIC_BASE_PATH ?? ''

const areaBasePaths: Record<AreaId, string> = {
    arbeidsgiver: process.env.NEXT_PUBLIC_BASE_PATH_ARBEIDSGIVER ?? '',
    arbeidstaker: process.env.NEXT_PUBLIC_BASE_PATH_ARBEIDSTAKER ?? '',
    samarbeidspartner: process.env.NEXT_PUBLIC_BASE_PATH_SAMARBEIDSPARTNER ?? '',
}

function localePrefix(locale: AppLocale): string {
    return locale === defaultLocale ? '' : `/${locale}`
}

/** Til fetch og auth, som ikke legger på basePath selv. */
export function withBasePath(path: string): string {
    return `${basePath}${path}`
}

/** Til <Link> og router.push, som legger på basePath selv. */
export function appPath(area: AreaId, prefix = '', path = ''): string {
    return `${prefix}${currentArea ? '' : `/${area}`}${path}` || '/'
}

/** Til rene anker-tagger og dekoratøren, som trenger basePath eksplisitt. */
export function areaPath(area: AreaId, prefix = '', path = ''): string {
    return (currentArea ? `${areaBasePaths[area]}${prefix}${path}` : appPath(area, prefix, path)) || '/'
}

/** Utskriftsendepunktene for gjeldende sti. Området er bakt inn i prod, men ligger som første segment lokalt. */
function utskriftPath(pathname: string, endepunkt: string): string | undefined {
    const parts = pathname.split('/').filter(Boolean)
    const locale = locales.includes(parts[0] as AppLocale) ? (parts.shift() as AppLocale) : defaultLocale
    const area = currentArea ?? areaIds.find((id) => id === parts[0])
    return area && areaPath(area, localePrefix(locale), endepunkt)
}

export function pdfPath(pathname: string): string | undefined {
    return utskriftPath(pathname, '/pdf')
}

export function excelPath(pathname: string): string | undefined {
    return utskriftPath(pathname, '/excel')
}

export function availableLanguages(area: AreaId) {
    return locales.map((locale) => ({
        locale,
        url: areaPath(area, localePrefix(locale)),
        handleInApp: false,
    }))
}

/** Login-proxy på appens egen ingress. `redirect` må være en URL-enkodet absolutt sti. */
export function loginPath(area: AreaId, prefix = ''): string {
    return `${withBasePath('/oauth2/login')}?redirect=${encodeURIComponent(areaPath(area, prefix))}`
}
